<script setup lang="ts">
import { openToPhrase } from '../lib/audience'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAppStore } from '../store'
import { requestAccess, getMailState, type RequestResult, type MailState } from '../api'
import { guessDeviceName } from '../lib/device'

/**
 * Asking for the activation mail: the address, a name for this device, and what came back
 * said in words. It stands on its own because it is needed in two places -- the way in on
 * first use, and the Registratie section in Settings.
 *
 * The link is followed somewhere else: in the mail programme, often on another device. The
 * app therefore cannot wait for an event to tell it -- an iOS app returning from Safari is
 * as likely to be restored without firing one as with -- so while a mail is outstanding this
 * asks the server every few seconds. And the outstanding request is remembered, because the
 * component is unmounted the moment the reader looks at another screen, and coming back to
 * an empty box makes it look as if nothing was ever sent.
 *
 * The same polling asks what became of the mail. A full mailbox or an address the other side
 * does not know comes back as a bounce a minute or so later, and the server reads those (see
 * bounces.ts); without this the reader would wait half an hour for a mail that was never
 * going to arrive. Not every refusal comes back, so after a few minutes of silence the screen
 * says where else to look.
 */

const PENDING_KEY = 'quicktide.pending'
const POLL_MS = 4000
/** Long enough for a mail to arrive and be read; the link itself lasts a day */
const WAIT_MS = 30 * 60_000
/** When a mail that has not arrived is more likely in the spam than still on its way */
const HINT_MS = 5 * 60_000

const store = useAppStore()
const email = ref('')
const deviceName = ref('')
const busy = ref(false)
const result = ref<RequestResult | null>(null)
/** When the mail we are waiting for was asked for, null when we are not waiting */
const waitingSince = ref<number | null>(null)
/** What the server has heard back about the mail */
const delivery = ref<MailState | null>(null)
const now = ref(Date.now())

/** A bounce, a delay, or after a while the places a missing mail usually is */
const deliveryMessage = computed(() => {
  const d = delivery.value
  if (d?.state === 'bounced') return store.t(d.reason === 'full' ? 'bouncedFull' : d.reason === 'unknown' ? 'bouncedUnknown' : 'bouncedRejected')
  if (!waitingSince.value) return ''
  if (d?.state === 'delayed') return store.t('mailDelayed')
  return now.value - waitingSince.value > HINT_MS ? store.t('noMailYet') : ''
})

/** Ask what became of the mail; a refusal ends the waiting, there is nothing left to wait for */
async function checkDelivery() {
  const d = await getMailState()
  if (!d) return
  delivery.value = d
  if (d.state === 'bounced') forget()
}
let timer: number | undefined

const message = computed(() => {
  const r = result.value
  if (!r) return ''
  // the mail came back: "a mail is on its way" would now be untrue, and the notice below says why
  if (delivery.value?.state === 'bounced') return ''
  // Vol account: de mail gaat toch, en op de pagina achter de link kan een toestel weg.
  if (r.status === 'sent' && r.atLimit) return `${store.tv('sentMsg', email.value)} ${store.tv('limitMsg', String(r.maxRegistrations ?? ''))}`
  if (r.status === 'sent') return store.tv('sentMsg', email.value)
  if (r.status === 'unknown') return store.t('unknownMsg')
  if (r.status === 'closed') {
    // the answer names who may, else the licence does; failing both, say it without names
    const who = openToPhrase(r.openTo, store.settings.locale) ?? store.openTo
    return who ? store.tv('closedMsgFor', who) : store.t('closedMsg')
  }
  if (r.status === 'expired') return store.t('expiredMsg')
  if (r.status === 'limit') return store.tv('limitMsg', String(r.maxRegistrations ?? ''))
  if (r.status === 'invalid') return store.t('invalidMsg')
  if (r.status === 'mail-failed') return store.t('mailFailedMsg')
  return store.t('requestFailedMsg')
})

function remember(address: string) {
  waitingSince.value = Date.now()
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ email: address, at: waitingSince.value }))
  } catch {
    // a browser that will not store it still polls for as long as this screen is open
  }
}

function forget() {
  waitingSince.value = null
  stopPolling()
  try {
    localStorage.removeItem(PENDING_KEY)
  } catch {
    // nothing to clear
  }
}

function stopPolling() {
  if (timer) window.clearInterval(timer)
  timer = undefined
}

/** Ask the server until the link has been followed, or until waiting stops making sense */
function poll() {
  stopPolling()
  timer = window.setInterval(async () => {
    now.value = Date.now()
    if (store.registered) return forget()
    if (waitingSince.value && Date.now() - waitingSince.value > WAIT_MS) return stopPolling()
    await store.refreshLicence()
    if (store.registered) return forget()
    await checkDelivery()
  }, POLL_MS)
}

async function send() {
  busy.value = true
  result.value = null
  delivery.value = null
  const address = email.value.trim()
  try {
    result.value = await requestAccess(address, deviceName.value.trim(), store.settings.locale)
    if (result.value.status === 'sent') {
      remember(address)
      poll()
    }
  } finally {
    busy.value = false
  }
}

/** The link is followed wherever the mail was opened, so this device has to go and look */
async function check() {
  busy.value = true
  try {
    await store.refreshLicence()
    if (store.registered) forget()
  } finally {
    busy.value = false
  }
}

onMounted(async () => {
  // opening this screen is itself a reason to ask: the answer may have arrived while it was shut
  void store.refreshLicence()
  let pending: { email?: string; at?: number } | null = null
  try {
    pending = JSON.parse(localStorage.getItem(PENDING_KEY) ?? 'null')
  } catch {
    pending = null
  }
  if (pending?.email && pending.at && Date.now() - pending.at < WAIT_MS) {
    // a mail is still outstanding: say so rather than showing an empty box
    email.value = pending.email
    waitingSince.value = pending.at
    result.value = { status: 'sent' }
    void checkDelivery()
    poll()
  } else if (pending) {
    forget()
  }
  deviceName.value ||= await guessDeviceName()
})

onUnmounted(stopPolling)
</script>

<template>
  <label class="reg">
    <span class="small muted">{{ store.t('emailLabel') }}</span>
    <input v-model="email" class="search" type="email" inputmode="email" autocomplete="email"
           autocapitalize="none" spellcheck="false" placeholder="naam@voorbeeld.nl" @keyup.enter="send()">
  </label>
  <label class="reg">
    <span class="small muted">{{ store.t('deviceNameLabel') }}</span>
    <input v-model="deviceName" class="search" type="text" maxlength="60" autocomplete="off">
  </label>
  <p class="small muted">{{ store.t('emailHelp') }}</p>
  <div class="setting">
    <span class="small muted">{{ message }}</span>
    <button class="primary" :disabled="busy || !email.trim()" @click="send()">
      {{ busy ? store.t('sending') : store.t('sendLink') }}
    </button>
  </div>
  <p v-if="result?.status === 'sent' && result.mailed === false" class="notice warn small">{{ store.t('notMailedMsg') }}</p>
  <p v-if="deliveryMessage" class="notice small" :class="{ warn: delivery?.state === 'bounced' }">{{ deliveryMessage }}</p>
  <div v-if="waitingSince" class="setting">
    <span class="small muted">{{ store.t('waitingForLink') }}</span>
    <button class="link" :disabled="busy" @click="check()">{{ store.t('checkAgain') }}</button>
  </div>
  <div v-if="result?.donateUrl" class="setting">
    <span class="small muted"></span>
    <a class="link" :href="result.donateUrl" target="_blank" rel="noopener">{{ store.t('toDonate') }}</a>
  </div>
</template>
